// Review suggested Customer List updates (Pat, Oct 6 2026: "update address, phone numbers and
// fax numbers from the internet — ask to confirm each one before inputting"). Each suggestion
// is shown beside what the list has NOW (the live list, so anything filled in since the lookup
// drops out by itself), with where it was found. Ticked fields are saved on "Apply"; a field
// that would REPLACE something already there starts unticked, and so does anything the lookup
// wasn't sure of.
import React, { useMemo, useState } from 'react';
import { apiFetch } from '../../services/apiClient';
import SUGGESTIONS from '../../data/customerSuggestions';

const FIELDS = [['name', 'Name'], ['address1', 'Address'], ['address2', 'City, State ZIP'], ['phone', 'Phone'], ['fax', 'Fax']];
const norm = v => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const CONF = { high: ['#dcfce7', '#166534', 'Sure'], medium: ['#fef9c3', '#854d0e', 'Fairly sure'], low: ['#fee2e2', '#b91c1c', 'Not sure — check'] };
const DONE_KEY = 'titan_customer_suggestions_done';
const readDone = () => { try { return JSON.parse(localStorage.getItem(DONE_KEY) || '{}') || {}; } catch { return {}; } };

// What's left to suggest for one customer against the live record.
function pending(s, live) {
  if (!live) return [];
  return FIELDS.filter(([k]) => s.suggest[k] && norm(s.suggest[k]) !== norm(live[k])).map(([k, label]) => ({ k, label, now: live[k] || '', found: s.suggest[k] }));
}
export function suggestionCount(clients) {
  const byId = Object.fromEntries((clients || []).map(c => [c.id, c]));
  const done = readDone();
  return SUGGESTIONS.filter(s => !done[s.client_id] && pending(s, byId[s.client_id]).length).length;
}

export default function CustomerSuggestions({ clients, onSaved, onClose }) {
  const byId = useMemo(() => Object.fromEntries((clients || []).map(c => [c.id, c])), [clients]);
  const [done, setDone] = useState(readDone);
  const [ticks, setTicks] = useState({});      // `${id}:${field}` → bool (unset = the default)
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const mark = (id, how) => setDone(d => { const n = { ...d, [id]: how }; try { localStorage.setItem(DONE_KEY, JSON.stringify(n)); } catch { /* fine */ } return n; });

  const cards = SUGGESTIONS.map(s => ({ s, live: byId[s.client_id], rows: pending(s, byId[s.client_id]) }));
  const open = cards.filter(c => c.live && c.rows.length && !done[c.s.client_id]);
  const notesOnly = cards.filter(c => c.live && !c.rows.length && !Object.keys(c.s.suggest).length);
  const isTicked = (c, r) => { const t = ticks[`${c.s.client_id}:${r.k}`]; return t === undefined ? (!String(r.now).trim() && c.s.confidence !== 'low') : t; };

  async function apply(c) {
    const body = {};
    c.rows.forEach(r => { if (isTicked(c, r)) body[r.k] = r.found; });
    if (!Object.keys(body).length) { setErr(`Nothing ticked for ${c.live.name}.`); return; }
    setBusy(c.s.client_id); setErr('');
    try {
      const saved = await apiFetch(`/clients/${c.s.client_id}`, { method: 'PATCH', body: JSON.stringify(body) });
      onSaved && onSaved(saved);
      mark(c.s.client_id, 'applied');
    } catch (e) { setErr(e?.error || e?.message || `Could not save ${c.live.name}.`); }
    setBusy('');
  }

  const box = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 5200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '3vh 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#f9fafb', borderRadius: 14, width: 860, maxWidth: '100%', padding: 18, boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>🔎 Suggested updates from the internet</div>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
              Looked up Oct 6 2026. Each one is beside what the list has now — tick what's right and press Apply; nothing is saved otherwise.
              A box that would replace something already there starts unticked, and so does anything marked “Not sure”.
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {err && <div style={{ color: '#b91c1c', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{err}</div>}
        {open.length === 0 && <div style={{ ...box, color: '#15803d', fontWeight: 700 }}>✓ Nothing left to review.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {open.map(c => {
            const [bg, fg, label] = CONF[c.s.confidence] || CONF.low;
            return (
              <div key={c.s.client_id} style={box}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginRight: 'auto' }}>{c.live.name}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 800, padding: '2px 9px', borderRadius: 999, background: bg, color: fg }}>{label}</span>
                </div>
                {c.s.note && <div style={{ fontSize: 12.5, color: '#4b5563', margin: '5px 0 8px', lineHeight: 1.45 }}>{c.s.note}</div>}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {c.rows.map(r => (
                      <tr key={r.k} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ width: 28, padding: '6px 0' }}>
                          <input type="checkbox" checked={isTicked(c, r)} onChange={e => setTicks(t => ({ ...t, [`${c.s.client_id}:${r.k}`]: e.target.checked }))} />
                        </td>
                        <td style={{ width: 120, color: '#6b7280', fontWeight: 700, fontSize: 12 }}>{r.label}</td>
                        <td style={{ color: r.now ? '#6b7280' : '#c2410c', textDecoration: r.now && isTicked(c, r) ? 'line-through' : 'none' }}>{r.now || 'blank'}</td>
                        <td style={{ width: 20, color: '#9ca3af' }}>→</td>
                        <td style={{ fontWeight: 700, color: '#111827' }}>{r.found}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11.5, color: '#9ca3af', marginRight: 'auto' }}>
                    Found on: {c.s.sources.length ? c.s.sources.slice(0, 3).map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" style={{ color: '#2563eb', marginRight: 8 }}>{(() => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return 'link'; } })()}</a>) : '—'}
                  </span>
                  <button onClick={() => mark(c.s.client_id, 'skipped')} disabled={busy === c.s.client_id} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', color: '#6b7280' }}>Skip</button>
                  <button onClick={() => apply(c)} disabled={busy === c.s.client_id} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>{busy === c.s.client_id ? 'Saving…' : 'Apply ticked'}</button>
                </div>
              </div>
            );
          })}
        </div>
        {notesOnly.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <button onClick={() => setShowNotes(v => !v)} style={{ background: 'none', border: 'none', color: '#6b7280', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', padding: 0 }}>{showNotes ? '▲' : '▼'} {notesOnly.length} with nothing new found (notes)</button>
            {showNotes && notesOnly.map(c => <div key={c.s.client_id} style={{ fontSize: 12.5, color: '#4b5563', marginTop: 6 }}><b>{c.live.name}:</b> {c.s.note}</div>)}
          </div>
        )}
        {Object.keys(done).length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
            {Object.values(done).filter(v => v === 'applied').length} applied · {Object.values(done).filter(v => v === 'skipped').length} skipped
            <button onClick={() => { setDone({}); try { localStorage.removeItem(DONE_KEY); } catch { /* fine */ } }} style={{ marginLeft: 10, background: 'none', border: 'none', color: '#2563eb', fontSize: 12, cursor: 'pointer', padding: 0 }}>show the skipped ones again</button>
          </div>
        )}
      </div>
    </div>
  );
}
